import { QUESTION_TYPE } from "./constants";

export function shadeColor(color, percent) {
  let R = parseInt(color.substring(1, 3), 16);
  let G = parseInt(color.substring(3, 5), 16);
  let B = parseInt(color.substring(5, 7), 16);

  R = Math.floor((R * (100 + percent)) / 100);
  G = Math.floor((G * (100 + percent)) / 100);
  B = Math.floor((B * (100 + percent)) / 100);

  R = R < 255 ? R : 255;
  G = G < 255 ? G : 255;
  B = B < 255 ? B : 255;

  const RR =
    R.toString(16).length === 1 ? "0" + R.toString(16) : R.toString(16);
  const GG =
    G.toString(16).length === 1 ? "0" + G.toString(16) : G.toString(16);
  const BB =
    B.toString(16).length === 1 ? "0" + B.toString(16) : B.toString(16);

  return "#" + RR + GG + BB;
}

/**
 * TODO: this is currently an O(N**2) function, don't use with peer lists, it's currently
 * being used to find intersection between list of role names where the complexity shouldn't matter much.
 */
export const arrayIntersection = (a, b) => {
  if (a === undefined || b === undefined) {
    return [];
  }
  // ensure "a" is the bigger array
  if (b.length > a.length) {
    let t = b;
    b = a;
    a = t;
  }
  return a.filter(function (e) {
    return b.indexOf(e) > -1;
  });
};

export const getMetadata = metadataString => {
  try {
    return metadataString === "" ? {} : JSON.parse(metadataString);
  } catch (error) {
    return {};
  }
};

export const metadataProps = function (peer) {
  return {
    isHandRaised: getMetadata(peer.metadata)?.isHandRaised,
  };
};

export const isScreenshareSupported = () => {
  return typeof navigator.mediaDevices.getDisplayMedia !== "undefined";
};

export const getRoutePrefix = () => {
  return window.location.pathname.startsWith("/streaming") ? "/streaming" : "";
};

export const isStreamingKit = () => {
  return window.location.pathname.startsWith("/streaming");
};

export const isPMDT = () => {
  const p = window.location.pathname.toLowerCase();
  const type = (window.localStorage.getItem("type") || "").toLowerCase();
  return p.includes("/pmdt") || type === "pmdt";
};

export const isMDT = () => {
  const p = window.location.pathname.toLowerCase();
  const type = (window.localStorage.getItem("type") || "").toLowerCase();
  return (p.includes("/mdt") && !isPMDT()) || type === "mdt";
};

export const isInternalRole = role => role && role.startsWith("__internal");

export const metadataPayloadParser = payload => {
  try {
    const data = window.atob(payload);
    const parsedData = JSON.parse(data);
    return parsedData;
  } catch (e) {
    return { payload };
  }
};

export const isValidURL = url => {
  const urlPattern = new RegExp(/^(ftp|http|https):\/\/[^ "]+$/);
  return !!urlPattern.test(url);
};

const compareArrays = (a, b) => {
  if (a.length !== b.length) return false;
  else {
    // Comparing each element of your array
    for (var i = 0; i < a.length; i++) {
      if (a[i] !== b[i]) {
        return false;
      }
    }
    return true;
  }
};

export const checkCorrectAnswer = (answer, localPeerResponse, type) => {
  if (type === QUESTION_TYPE.SINGLE_CHOICE) {
    return answer?.option === localPeerResponse?.option;
  } else if (type === QUESTION_TYPE.MULTIPLE_CHOICE) {
    return (
      answer?.options &&
      localPeerResponse?.options &&
      compareArrays(answer?.options, localPeerResponse?.options)
    );
  }
};

export const isValidTextInput = (text, minLength = 2, maxLength = 100) => {
  return text && text.length >= minLength && text.length <= maxLength;
};

/**
 * Normalize MDT/PMDT disease arrays to a common shape used by presentation UI.
 * For MDT responses we return items as-is. For PMDT responses we prefer the
 * nested `mdt_room_disease` object but keep some top-level fields (clinic,
 * public_mdt_case_id) so callers can access clinic info for grouping.
 *
 * @param {Array} items - array returned by API (res.data.data)
 * @param {boolean} isPmdt
 * @returns {Array} normalized array of disease objects
 */
export const normalizeDiseaseItems = (items = [], isPmdt = false) => {
  if (!Array.isArray(items)) return [];
  if (!isPmdt) return items;

  return items
    .map(item => {
      if (!item) return null;
      const inner = item.mdt_room_disease || {};
      const normalized = {
        ...inner,
        id: item.id || inner.id,
        mdt_room_disease_id: inner.id || null,
        patient_name: inner.patient_name || item.patient_name,
        patient_diagnostic: inner.patient_diagnostic || item.patient_diagnostic,
        created_at: inner.created_at || item.created_at || item.updated_at,
        doctor: inner.doctor || item.doctor,
        concluding: inner.concluding || item.concluding,
        operation: inner.operation || item.operation,
        multilang: inner.multilang || item.multilang,
        clinic: item.clinic || inner.clinic || null,
        medical_record:
          inner.medical_record ||
          item.medical_record ||
          (item.clinic && item.clinic.id
            ? { clinic_id: item.clinic.id }
            : inner.clinic && inner.clinic.id
              ? { clinic_id: inner.clinic.id }
              : null),
        type: inner.type || item.type || 0,
        status: item.status,
        __public_mdt_case: item,
      };

      return normalized;
    })
    .filter(Boolean);
};

/**
 * Given a flat list of diseases (normalized), for PMDT display insert clinic
 * placeholder objects before each clinic group's items. The placeholder has
 * `__isClinicPlaceholder: true` and contains clinicName and count.
 *
 * @param {Array} diseases - normalized disease array
 * @returns {Array} array with placeholders inserted
 */
export const insertClinicPlaceholders = diseases => {
  if (!Array.isArray(diseases)) return [];
  const groups = diseases.reduce((acc, d) => {
    const id = (d?.clinic && d.clinic.id) || 0;
    if (!acc[id]) acc[id] = { clinic: d.clinic || null, items: [] };
    acc[id].items.push(d);
    return acc;
  }, {});

  const result = [];
  Object.keys(groups).forEach(key => {
    const g = groups[key];
    const name = g.clinic?.name || g.clinic?.org_name || "" || "";
    const count = g.items.length;
    result.push({
      __isClinicPlaceholder: true,
      id: `__clinic_${key}__start`,
      __clinicId: key,
      __clinicName: name,
      __clinicCount: count,
      __clinicPlaceholderType: "start",
      type:
        g.items && g.items[0] && typeof g.items[0].type !== "undefined"
          ? g.items[0].type
          : 0,
    });
    result.push(...g.items);
    result.push({
      __isClinicPlaceholder: true,
      id: `__clinic_${key}__end`,
      __clinicId: key,
      __clinicName: name,
      __clinicCount: count,
      __clinicPlaceholderType: "end",
      type:
        g.items && g.items[0] && typeof g.items[0].type !== "undefined"
          ? g.items[0].type
          : 0,
    });
  });

  return result;
};

export const parseClinicPlaceholder = showingId => {
  if (typeof showingId !== "string" || !showingId.startsWith("__clinic_")) {
    return { clinicId: null, placeholderType: null };
  }
  const m = showingId.match(/^__clinic_([^_]+)(?:__(start|end))?$/);
  const clinicId = m ? m[1] : showingId.replace("__clinic_", "");
  const placeholderType = m && m[2] ? m[2] : "start";
  return { clinicId, placeholderType };
};
